import React, { useRef, useEffect } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import { Style, Stroke, Fill, Text } from "ol/style";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Polygon from "ol/geom/Polygon";
import { fromLonLat } from "ol/proj";
import Overlay from "ol/Overlay";
import { defaults as defaultControls } from "ol/control";
import ImageLayer from "ol/layer/Image";
import ImageStatic from "ol/source/ImageStatic";
import Projection from "ol/proj/Projection";

declare const window: any;

const MapComponent = ({ data, isSelectedVIA, isMapRender, isSelectedPOI }) => {
  const mapRef = useRef(null);
  const mapInstance: any = useRef(null);

  // --- MODO MAPA NORMAL ---
  useEffect(() => {
    if (isMapRender) return;
    if (!mapRef.current || !data) return;

    const features: Feature[] = [];

    // Polígono de delimitación
    if (data?.coordenates_delimitations?.length) {
      const polygonCoordinates = data.coordenates_delimitations.map((coord) =>
        fromLonLat([coord.lng, coord.lat])
      );
      const polygon = new Polygon([polygonCoordinates]);
      const polygonFeature = new Feature({ geometry: polygon });
      polygonFeature.setStyle(
        new Style({
          stroke: new Stroke({ color: data?.color_delimitation, width: 2 }),
          fill: new Fill({ color: hexToRGBA(data?.color_delimitation, 0.45) }),
        })
      );
      features.push(polygonFeature);
    }

    console.log(isSelectedPOI);

    // POIs
    if (isSelectedPOI?.length > 0) {
      isSelectedPOI.forEach((pois) => {
        pois?.PoisArray?.forEach((poi) => {
          const poiLat = poi.center.lat;
          const poiLng = poi.center.lng;

          const nuevaLatitud = poiLat * 1.00008;
          const nuevaLongitud = poiLng;

          // línea banderín
          const lineFeature = new Feature({
            geometry: new LineString([
              fromLonLat([poiLng, poiLat]),
              fromLonLat([nuevaLongitud, nuevaLatitud]),
            ]),
          });
          lineFeature.setStyle(
            new Style({
              stroke: new Stroke({ color: "#000", width: 2 }),
            })
          );
          features.push(lineFeature);

          // etiqueta
          const textoFeature = new Feature({
            geometry: new Point(fromLonLat([nuevaLongitud, nuevaLatitud])),
            name: poi.name,
          });
          textoFeature.setStyle(
            new Style({
              text: new Text({
                text: poi.name,
                font: "12px Arial",
                fill: new Fill({ color: "#000" }),
                offsetY: -15,
                textAlign: "center",
                textBaseline: "middle",
                backgroundFill: new Fill({ color: "rgba(255,255,255,0.8)" }),
                backgroundStroke: new Stroke({
                  color: "rgba(255,255,255,1)",
                  width: 1,
                }),
                padding: [4, 8, 4, 8],
              }),
            })
          );
          features.push(textoFeature);
        });
      });
    }

    // Vías de acceso
    if (isSelectedVIA?.length > 0) {
      isSelectedVIA.forEach((via) => {
        const lineCoordinates = via.trace.map((point) =>
          fromLonLat([point.lng, point.lat])
        );
        const lineFeature = new Feature({
          geometry: new LineString(lineCoordinates),
          name: via.name,
        });
        lineFeature.setStyle(
          new Style({
            stroke: new Stroke({ color: via.color, width: 6 }),
          })
        );
        features.push(lineFeature);
      });
    }

    const vectorSource = new VectorSource({ features });
    const vectorLayer = new VectorLayer({ source: vectorSource });

    const map = new Map({
      target: mapRef.current,
      layers: [new TileLayer({ source: new OSM() }), vectorLayer],
      view: new View({
        center: fromLonLat([data.center.lng, data.center.lat]),
        zoom: data.defaultZoom,
      }),
      controls: defaultControls({ zoom: false }),
    });

    mapInstance.current = map;

    // Logo del proyecto
    if (data?.logo_proyecto) {
      const iconContainer = document.createElement("div");
      iconContainer.style.width = "70px";
      iconContainer.style.height = "70px";
      iconContainer.style.backgroundImage = `url(${
        window.hybrid ? "./" : "https://d1bsymnertnea6.cloudfront.net/"
      }${data.logo_proyecto.icon})`;
      iconContainer.style.backgroundSize = "contain";
      iconContainer.style.backgroundRepeat = "no-repeat";

      const iconOverlay = new Overlay({
        position: fromLonLat([
          data.logo_proyecto.center.lng,
          data.logo_proyecto.center.lat,
        ]),
        positioning: "center-center",
        element: iconContainer,
        stopEvent: false,
      });

      map.addOverlay(iconOverlay);
    }

    // 🔹 Ajustar el encuadre automáticamente
    // 🔹 Ajustar el encuadre automáticamente
    if (features.length > 0) {
      const extent = vectorSource.getExtent();

      if (isSelectedVIA?.length > 0 && data?.zoom_vias) {
        // Caso: hay vías seleccionadas → usar zoom por defecto de data.zoom_vias
        map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 800 });
        map.getView().setZoom(parseInt(data.zoom_vias));
      } else if (isSelectedPOI?.length === 1 && isSelectedPOI[0].zoom) {
        // Caso: un solo grupo de POIs con zoom definido
        const poiGroup = isSelectedPOI[0];
        map
          .getView()
          .setCenter(
            fromLonLat([
              parseFloat(poiGroup.longitud),
              parseFloat(poiGroup.latitud),
            ])
          );
        map.getView().setZoom(parseInt(poiGroup.zoom));
      } else {
        // Caso general: encuadrar todos los features
        map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 800 });
      }
    }

    return () => {
      map.setTarget(undefined);
    };
  }, [data, isSelectedVIA, isSelectedPOI, isMapRender]);

  // --- MODO MAPA ILUSTRADO ---
  useEffect(() => {
    if (!isMapRender) return;
    if (!mapRef.current || !data?.mapa_ilustrado_vias) return;

    const img = new Image();
    img.src = `${
      window.hybrid ? "" : "https://d1bsymnertnea6.cloudfront.net/"
    }${data.mapa_ilustrado_vias.replace(/ /g, "%20")}`;

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const extent: [number, number, number, number] = [0, 0, width, height];

      const projection = new Projection({
        code: "custom-image",
        units: "pixels",
        extent: extent,
      });

      const imageLayer = new ImageLayer({
        source: new ImageStatic({
          url: img.src,
          projection: projection,
          imageExtent: extent,
        }),
      });

      const map = new Map({
        target: mapRef.current,
        layers: [imageLayer],
        view: new View({
          projection: projection,
          center: [width / 2, height / 2],
          extent: extent,
          minZoom: 0,
          maxZoom: 8,
        }),
        controls: [],
      });

      // 🔹 Encajar la imagen al viewport
      map.getView().fit(extent, { size: map.getSize(), duration: 500 });

      mapInstance.current = map;
    };

    return () => {
      if (mapInstance.current) mapInstance.current.setTarget(undefined);
    };
  }, [isMapRender, data]);

  const hexToRGBA = (hex, alpha = 1) => {
    hex = hex.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div
      className="map-container"
      ref={mapRef}
      style={{ position: "relative", height: "100%", width: "100%" }}
    />
  );
};

export default MapComponent;
